import { Component, EventEmitter, forwardRef, Input, Output, inject } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import _ from 'lodash';

import { LocalStatus } from '../../version-control/meta-info.model';
import { NavigationService } from '../../navbar/navigation.service';
import { DirtyFormGuardConnectorService } from '../../common/services/dirty-form-guard-connector.service';
import { DeleteAttachmentObj, ResourceItem, SaveAttachmentObj } from '../../patterns/pattern-attachment.model';

@Component({
    selector: 'adm4-attachment-property-list',
    template: `
    @if (isListDisplayed) {
      <div class='file-list'>
        <!-- existing items-->
        @for (resource of patternResourceItemList; track resource) {
          <ng-template #popupLocalChanges>
            <adm4-local-changes-popup [metaInfo]="resource" (publishClick)='openPublishProjectDialog()'>
            </adm4-local-changes-popup>
          </ng-template>
          @if (!isHiddenItem(resource, propertyKey, deleteResourceList)) {
            <div class='file-list-item'>
              <button type='button' class='admn4-button-icon'
                [ngbTooltip]="downloadHint" placement="top-left"
                (click)='downloadItem(resource)'
                [style.color]='!showDownloadButton(resource, publish) ? "transparent" : ""'
                [disabled]='!showDownloadButton(resource, publish)'>
                <i class="fa fa-download" aria-hidden="true"></i>
              </button>
              <ng-template #downloadHint><span>Download <strong>{{resource.resourceName}}</strong></span></ng-template>
              @if (publish) {
                <button type="button" class='admn4-button-icon'
                  [ngbTooltip]="diffTooltip" placement="top-left" [disableTooltip]="!showDiffButton(resource)"
                  [style.color]='!showDiffButton(resource) ? "transparent" : ""'
                  [disabled]="!showDiffButton(resource)"
                  (click)="diffItem(resource)">
                  <mat-icon class="size-20 diff-icon">compare_arrows</mat-icon>
                </button>
              }
              <ng-template #diffTooltip><span>Compare <strong>{{resource.resourceName}}</strong></span></ng-template>
              @if (!publish && !readOnly) {
                <a tabindex="0" class='file-name' [ngClass]='fileNameClass(resource)' (click)="editItem(resource)"
                  [ngbTooltip]="editHint" placement="top-left"
                >{{resource.resourceName}}</a>
              } @else {
                <span class="file-name" [ngClass]='fileNameClass(resource)'>
                  {{resource.resourceName}}
                </span>
              }
              <ng-template #editHint><span>Click to edit {{resource.resourceName}}</span></ng-template>
              @if (shouldDisplayModificationDate(resource)) {
                <div class='file-modification-date'
                  [ngbTooltip]='popupLocalChanges' [disableTooltip]='!versioned' placement='left' tooltipClass="adm4-tooltip behind-dialog">
                  {{getModificationDateOfResource(resource)| customDate}}
                </div>
              }
              @if (showDownloadButton(resource, publish)) {
                <button type='button' class='admn4-button-icon' (click)='deleteResourceItem(resource)'
                  [ngbTooltip]="deleteExistingHint" placement="top-right">
                  @if (!readOnly) {
                    <i [class]='"fa fa-times-circle-o"' aria-hidden="true"></i>
                  }
                </button>
              }
              <ng-template #deleteExistingHint><span>Delete {{resource.resourceName}}</span></ng-template>
            </div>
          }
        }
        <!-- new items-->
        @if (!publish) {
          @for (attachment of newAttachmentList; track attachment) {
            @if (attachment.propertyKey === propertyKey) {
              <div class='file-list-item'>
                @if (!readOnly) {
                  <button type='button' class='admn4-button-icon' disabled
                    [ngbTooltip]="newFileHint" placement="top-left">
                    <i class="fa fa-file-o" aria-hidden="true"></i>
                  </button>
                }
                <ng-template #newFileHint><span>New file</span></ng-template>
                <div class='file-name'
                  [ngbTooltip]="modifyHint" placement="top-left"
                >{{attachment.fileToSave.name}}</div>
                <ng-template #modifyHint><span>Save the file</span></ng-template>
                @if (!readOnly) {
                  <button type='button' class='admn4-button-icon' (click)='removeFileItem.emit(attachment)'
                    [ngbTooltip]="deleteAddedHint" placement="top-right">
                    <i class="fa fa-times-circle-o" aria-hidden="true"></i>
                  </button>
                }
                <ng-template #deleteAddedHint><span>Delete {{attachment.fileToSave.name}}</span></ng-template>
              </div>
            }
          }
        }
      </div>
    }
    `,
    styleUrls: ['./attachment-property-list.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AttachmentPropertyListComponent),
            multi: true
        }],
    standalone: false
})
export class AttachmentPropertyListComponent {
  private navigationService = inject(NavigationService);
  private formGuardConnectorService = inject(DirtyFormGuardConnectorService);

  @Input() propertyKey: string;
  @Input() meta = false;
  @Input() newAttachmentList: SaveAttachmentObj[];
  @Input() patternResourceItemList: ResourceItem[];
  @Input() deleteResourceList = [] as  DeleteAttachmentObj[];
  @Input() readOnly: boolean;
  /*TODO: this can be decided based on the instance of propertyContextService - clean up*/
  @Input() publish: boolean;
  @Input() local: boolean;
  @Input() versioned: boolean;
  @Output() removeFileItem = new EventEmitter<SaveAttachmentObj>();
  @Output() deleteResource = new EventEmitter<ResourceItem>();
  @Output() downloadResource = new EventEmitter<ResourceItem>();
  @Output() editResource = new EventEmitter<ResourceItem>();
  @Output() diffResource = new EventEmitter<ResourceItem>();


  get isListDisplayed(): boolean {
    return (!_.isEmpty(this.patternResourceItemList) && !this.patternResourceItemList.every(resource => this.isHiddenItem(resource, this.propertyKey, this.deleteResourceList))) || !_.isEmpty(this.newAttachmentList);
  }

  fileNameClass(resourceItem: ResourceItem): string {
    switch (resourceItem.localStatus) {
      case LocalStatus.Deleted : {
        return this.local ? 'deleted' : '';
      }
      case LocalStatus.Modified: {
        return '';
      }
      case LocalStatus.Added : {
        return this.local ? '' : 'hidden';
      }
      case LocalStatus.Unmodified: {
        return '';
      }
      default:
        return '';
    }
  }

  deleteResourceItem(resourceItem: ResourceItem) {
    this.deleteResource.emit(resourceItem);
  }

  public downloadItem(item: ResourceItem) {
    this.downloadResource.emit(item);
  }

  public editItem(item: ResourceItem) {
    this.editResource.emit(item);
  }

  public diffItem(item: ResourceItem) {
    this.diffResource.emit(item);
  }

  showDownloadButton(resource: ResourceItem, publish: boolean): boolean {
    if (!publish) {
      return true;
    }
    switch (resource.localStatus) {
      case LocalStatus.Added : {
        return this.local;
      }
      case LocalStatus.Deleted: {
        return !this.local;
      }
      default:
        return true;
    }
  }

  showDiffButton(resource: ResourceItem) {
    if (!this.publish) {
      return false;
    }
    if (
      LocalStatus.Modified === resource.localStatus
      || (this.local && LocalStatus.Added === resource.localStatus)
      || (!this.local && LocalStatus.Deleted === resource.localStatus)
    ) {
      return true;
    }
    return false;
  }

  /** An item is hidden if it is deleted or is overwritten in the local changes. */
  isHiddenItem(resource: ResourceItem, propertyKey: string, deleteResourceList: DeleteAttachmentObj[]): boolean {
    return this.isDeleted(deleteResourceList, resource, propertyKey) || this.isOverwritten(resource);
  }

  shouldDisplayModificationDate(resource: ResourceItem): boolean {
    return !this.publish && (!_.isNil(resource.localDate) || !_.isNil(resource.remoteDate));
  }

  private isDeleted(deleteResourceList: DeleteAttachmentObj[], resource: ResourceItem, propertyKey: string) {
    return !_.isNil(_.find(deleteResourceList, (item) => {
      return item.fileName === resource.resourceName && item.propertyKey === propertyKey;
    }));
  }

  private isOverwritten(resource: ResourceItem) {
    return !_.isNil(_.find(this.newAttachmentList, (item) => {
      return item.fileToSave.name === resource.resourceName;
    }));
  }

  getModificationDateOfResource(resource: ResourceItem): string {
    return resource.localDate || resource.remoteDate || '';
  }

  openPublishProjectDialog(): void {
    this.formGuardConnectorService.doIfConfirmed(() => this.navigationService.navigateToPublishProjectWindow());
  }
}
